<template>
  <div class="theme-editor__backdrop" />
  <aside
    v-if="currentEditTheme"
    class="theme-editor"
  >
    <div class="theme-editor__header">
      <span class="theme-editor__title">
        <template v-if="isNewTheme">
          {{ t('news_page_themes_edit_popup.title') }}
        </template>
        <template v-else>
          {{ theme.name }}
        </template>
      </span>

      <button
        type="button"
        class="theme-editor__close"
        @click="handleCloseEditPopup"
      >
        <AvalIconCross
          class="theme-editor__close-icon"
          aria-hidden="true"
        />
      </button>
    </div>

    <div class="theme-editor__content">
      <AvalInputPrimary
        ref="inputNameRef"
        v-model.trim="currentEditTheme.name"
        :required="true"
        :placeholder="t('news_page_themes_edit_popup.placeholder.theme_name')"
        :label="t('news_page_themes_edit_popup.label.theme_name')"
      />

      <div class="theme-editor__rubrics">
        <div class="theme-editor__selected">
          <span class="theme-editor__label theme-editor__label--required">
            {{ t('news_page_themes_edit_popup.label.rubric') }}
          </span>

          <div
            v-if="selectedRubrics.length > 0"
            class="theme-editor__chips scroll"
          >
            <AvalChip
              v-for="item in selectedRubrics"
              :key="item.id"
              :label="item.name"
              :use-location-icon="false"
              @cancel="toggleTheme(item)"
            />
          </div>
        </div>

        <AvalInputSearchSecondary
          v-model="searchString"
          :placeholder="t('news_page_themes_edit_popup.placeholder.search')"
        />

        <div class="theme-editor__list">
          <AvalListView class="theme-editor__list-view scroll">
            <AvalListItem
              v-for="item in allRubrics"
              :key="item"
              v-model="currentEditTheme.rubric_ids"
              :item="item.id"
              :use-checkbox="true"
            >
              {{ item.name }}
            </AvalListItem>
          </AvalListView>
        </div>
      </div>
    </div>

    <NewsPagePopupTelegramBlock
      @move-to-settings="handleMoveToSettings"
      @close-edit-popup="emit('close-edit-popup')"
    />

    <div class="theme-editor__footer">
      <AvalButtonAccentPrimary
        :disabled="isSaveButtonDisabled"
        :pressed="isSaveButtonClicked"
        size="large"
        class="theme-editor__button"
        @click="saveChanges"
      >
        {{ t('news_page_themes_edit_popup.button.save') }}
      </AvalButtonAccentPrimary>
      <AvalButtonNeutralSecondary
        size="large"
        class="theme-editor__button"
        @click="handleCloseEditPopup"
      >
        {{ t('news_page_themes_edit_popup.button.cancel') }}
      </AvalButtonNeutralSecondary>
      <div class="theme-editor__footer-end">
        <AvalButtonAccentSecondary
          v-if="!props.isNewTheme"
          size="large"
          class="theme-editor__button theme-editor__button--delete"
          @click="emit('open-delete-popup')"
        >
          <template #icon-left>
            <IconTrash />
          </template>
          {{ t('news_page_themes_edit_popup.button.delete') }}
        </AvalButtonAccentSecondary>
      </div>
    </div>
  </aside>

  <!-- Окно подтверждения изменений -->
  <AvalModal
    v-if="isShowConfirmModal"
    :title="t('news_page_popup_delete.title.unsaved_changes')"
    :confirm-text="t('news_page_popup_delete.button.accept_unsaved')"
    :cancel-text="t('news_page_popup_delete.button.cancel_button')"
    teleport="#app"
    @cancel="isShowConfirmModal = false"
    @confirm="emit('close-edit-popup')"
  >
    {{ t('news_page_popup_delete.message.unsaved_changes') }}
  </AvalModal>

  <!-- Окно подтверждения изменений при переходе в настройки ТГ -->
  <AvalModal
    v-if="isShowConfirmModalSettings"
    :title="t('news_page_telegram_block.modal_unsaved.title')"
    :confirm-text="t('news_page_telegram_block.modal_unsaved.save')"
    :cancel-text="t('news_page_telegram_block.modal_unsaved.cancel')"
    teleport="#app"
    width="392px"
    @cancel="isShowConfirmModalSettings = false"
    @confirm="moveToSettings"
  >
    {{ t('news_page_telegram_block.modal_unsaved.message', currentEditTheme.name) }}
  </AvalModal>
</template>

<script setup>
import {defineProps, defineEmits, computed, ref, onMounted, nextTick} from "vue";
import { useRubricsStore } from "@/store/NewsFeed/rubrics";
import { t } from "@/controllerLK/AvalLocalization";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useThemesStore } from "@/store/NewsFeed/themes";
import NewsPagePopupTelegramBlock from "@/components/NewsPage/NewsPagePopup/NewsPagePopupTelegramBlock.vue";
import IconTrash from "@/components/Icons/IconTrash.vue";
import {useLK} from "@/controllerLK/LKstore";

const props = defineProps({
  theme: {
    type: [Object, null],
    required: true
  },
  isNewTheme: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["close-edit-popup", "open-delete-popup"]);

// stores
const themesStore = useThemesStore();
const rubricsStore = useRubricsStore();
const newsFeedStore = useNewsFeedStore();
const LKStore = useLK();

onMounted(async () => {
  currentEditTheme.value = JSON.parse(JSON.stringify(props.theme));

  defaultTheme = JSON.parse(JSON.stringify(props.theme));

  // Устанавливаем фокус на поле ввода названия, после загрузки темы.
  await nextTick();

  if (inputNameRef.value) {
    inputNameRef.value.focus();
  }
});

// Реф на поле ввода названия темы
const inputNameRef = ref(null);

// Текущая редактируемая тема.
const currentEditTheme = ref(null);
// Дефолтное состояние темы.
let defaultTheme = null;

// Флаг. Отображение окна несохраненных изменений при переходе в настройкик ТГ подписок.
const isShowConfirmModalSettings = ref(false);

// Флаг. Отображение окна подтверждения изменений.
const isShowConfirmModal = ref(false);

// Флаг. Клик на кнопку сохранения изменеий.
const isSaveButtonClicked = ref(false);

// Флаг. Активна ли кнопка для сохранения изменений
const isSaveButtonDisabled = computed(
    () => currentEditTheme.value.name.length === 0 ||
        currentEditTheme.value.rubric_ids.length === 0 ||
        !hasUnsavedChanges.value );

// Значение поисковой строки тем.
const searchString = ref("");

// Список включенных рубрик в тему.
const selectedRubrics = computed(
    () => rubricsStore.allRubrics.filter((rubric) => currentEditTheme.value.rubric_ids.includes(rubric.id)));

// Список всех рубрик со значением поисковой строки
const allRubrics = computed(() => {
  if (searchString.value.length === 0) {
    return rubricsStore.allRubrics.filter((rubric) => rubric.is_tracked);
  } else {
    return rubricsStore.allRubrics
        .filter((rubric) => rubric.is_tracked && rubric.name.toLowerCase().includes(searchString.value.toLowerCase()));
  }
});

// Функция включения/Выключения рубрики в теме.
const toggleTheme = (item) => {
  if (currentEditTheme.value.rubric_ids.includes(item.id)) {
    currentEditTheme.value.rubric_ids = currentEditTheme.value.rubric_ids.filter((rubric) => rubric !== item.id);
  } else {
    currentEditTheme.value.rubric_ids.push(item.id);
  }
};

// Обработчик закрытия окна с проверкой наличия изменений.
const handleCloseEditPopup = () => {
  if (hasUnsavedChanges.value) {
    isShowConfirmModal.value = true;
  } else {
    emit("close-edit-popup");
  }
};

// Обработчик перехода на страницу настроек подписок ТГ с проверкой наличия изменений.ч
const handleMoveToSettings = () => {
 if( hasUnsavedChanges.value ) {
   isShowConfirmModalSettings.value = true;
 } else {
    moveToSettings();
 }
};

// Событие перехода на страницу настроек подписок ТГ с сохранением темы.
const moveToSettings = () => {
  saveChanges();
  emit("close-edit-popup");
  const event = new CustomEvent("GoToProjectEdit", {detail: {
      projectId: LKStore.project.id,
      tab: "telegramm"
    }});
  window.dispatchEvent(event);
};

const hasUnsavedChanges = computed(() => JSON.stringify(defaultTheme) !== JSON.stringify(currentEditTheme.value));

// Функция сохранения изменений/создания темы.
async function saveChanges() {
  isSaveButtonClicked.value = true;
  if (currentEditTheme.value.name) {
    if (!props.isNewTheme) {
      await themesStore.updateTheme(currentEditTheme.value);
      emit("close-edit-popup");
      await themesStore.getAllThemes();
      await rubricsStore.getAllRubrics();
      await newsFeedStore.clearNewsFeed();
      await newsFeedStore.getNews();
    } else {
      await themesStore.addNewTheme(currentEditTheme.value);
      emit("close-edit-popup");
    }
  }
  isSaveButtonClicked.value = false;
}
</script>

<style lang="scss" scoped>
.theme-editor {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 999;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 600px;
  height: 100%;
  padding: 20px;
  background-color: var(--background-bg-white);
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);

  &__backdrop {
    position: fixed;
    inset: 0;
    z-index: 999;
    background-color: rgba(0, 0, 0, 0.5);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  &__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
    color: var(--text-text-black);
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    background-color: transparent;
    color: var(--icon-icon-secondary);
    cursor: pointer;

    &:hover {
      color: var(--icon-icon-on);
    }

    &:active {
      color: var(--icon-icon-primary, var(--icon-icon-on));
    }

    &-icon {
      width: 20px;
      height: 20px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 20px;
    flex: 1;
    min-height: 0;
  }

  &__rubrics {
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;
  }

  &__selected {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 250px;
  }

  &__label {
    font-weight: 500;
    font-size: 14px;
    line-height: 19.6px;
    color: var(--form-primary-title-text);

    &--required::after {
      content: " *";
      color: red;
    }
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    padding-right: 2px;
  }

  &__list {
    display: flex;
    flex: 1;
    min-height: 0;
    padding: 10px;
    border: 1px solid #dedede;
    border-radius: 8px;

    &-view {
      width: 100%;
    }
  }

  &__footer {
    display: flex;
    gap: 8px;

    &-end  {
      margin-left: auto;
    }
  }

  &__button {
    min-width: 133px;
  }
}
</style>